import { parsePath } from 'history';
import { useCallback } from 'react';
import { useFetchCountries } from './api';
import { useFetchUserAbout } from './user';
import { useLocation } from 'react-router-dom';

const DEFAULT_COUNTRY_CODE = 'US';

const useDefaultCountryCode = () => {
    const countriesQuery = useFetchCountries();
    const userAboutQuery = useFetchUserAbout();
    const userAllowedCountries = userAboutQuery.data?.allowed_countries;

    // Both queries must have answered in the shape read below, not merely with
    // something truthy.
    if (!countriesQuery.data || !Array.isArray(userAllowedCountries))
        return null;

    const userPreferredCountry = userAboutQuery.data.preferred_country;
    const userPreferredCountryInDatabase = countriesQuery.data.some(
        ({ text_id }) => text_id === userPreferredCountry,
    );

    if (
        userPreferredCountry &&
        userPreferredCountryInDatabase &&
        userAllowedCountries.includes(userPreferredCountry)
    ) {
        return userPreferredCountry;
    }

    const defaultCountry = countriesQuery.data.find(
        ({ text_id }) => text_id === DEFAULT_COUNTRY_CODE,
    );

    if (defaultCountry && userAllowedCountries.includes(DEFAULT_COUNTRY_CODE)) {
        return DEFAULT_COUNTRY_CODE;
    }

    return userAllowedCountries.find((user_allowed_country_text_id) =>
        countriesQuery.data.some(
            ({ text_id }) => user_allowed_country_text_id === text_id,
        ),
    );
};

const mutateToWithCountryParam = (to, countryParam, defaultCountryCode) => {
    if (!countryParam) return to;

    const countryCode = countryParam.toString().toUpperCase();
    const nextLocation = typeof to === 'string' ? parsePath(to) : to;
    const nextParams = new URLSearchParams(nextLocation.search);
    if (!nextParams.has('country')) nextParams.set('country', countryCode);
    if (nextParams.get('country').toString() === defaultCountryCode)
        nextParams.delete('country');
    return { ...nextLocation, search: nextParams.toString() };
};

const useCountryParam = () => {
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const countryParam = queryParams.get('country');
    const defaultCountryCode = useDefaultCountryCode();
    const currentCountryParam = (countryParam || defaultCountryCode || '')
        .toString()
        .toUpperCase();

    const mutateToWithCountryParamCallback = useCallback(
        (to) => {
            return mutateToWithCountryParam(
                to,
                countryParam,
                defaultCountryCode,
            );
        },
        [countryParam, defaultCountryCode],
    );

    return {
        currentCountryParam: currentCountryParam || null,
        mutateToWithCountryParam: mutateToWithCountryParamCallback,
    };
};

export default useCountryParam;
